import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { DxButtonModule, DxPopupModule } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { environment } from '../../../services/app-settings.service';
import { DkNavService } from '../../../services/dk-nav.service';
import { YardimListesiService } from './yardim-listesi.service';

/** Gen_YardimAnahtarOlustur yanıtı: geçidin gömülü sayfası ve tam site girişi (15 dk geçerli imzalı anahtarla). */
interface YardimAnahtarYanit {
  result?: boolean;
  errorMessage?: string;
  url?: string;
  siteUrl?: string;
}

/**
 * Ekran başlıklarındaki (dk-list-header / dk-form-header) "?" düğmesi: açık sekmenin yardımını merkezi yardım
 * sitesinden (yardim.dabto.com) iframe'li pencerede açar. Anahtarı müşterinin kendi Genservices'i imzalar;
 * yardım sitesi ayrıca şifre sormaz. Footer'daki "Yardım" (destek talebi) düğmesi ayrıdır, aynen kalır.
 * F1 de açık sekmenin yardımını açar. Düğme yalnız yardımı hazır ekranlarda görünür (YardimListesiService).
 */
@Component({
  selector: 'dk-yardim-dugmesi',
  standalone: true,
  imports: [CommonModule, DxButtonModule, DxPopupModule],
  template: `
    <dx-button
      *ngIf="hazir"
      stylingMode="text"
      icon="help"
      hint="Bu ekranın yardımı (F1)"
      [disabled]="yukleniyor"
      [elementAttr]="{ 'aria-label': 'Yardım', class: 'yardim-dugmesi' }"
      (onClick)="ac()"
    ></dx-button>
    <dx-popup
      *ngIf="adres"
      [visible]="true"
      title="Yardım"
      [width]="'min(960px, 96vw)'"
      [height]="'88vh'"
      [showCloseButton]="true"
      [dragEnabled]="true"
      [resizeEnabled]="true"
      [hideOnOutsideClick]="true"
      [toolbarItems]="araclar"
      (onHidden)="kapat()"
    >
      <div *dxTemplate="let _ of 'content'" class="yardim-cerceve">
        <iframe [src]="adres" title="Yardım" referrerpolicy="no-referrer"></iframe>
      </div>
    </dx-popup>
  `,
  styles: [
    `
      .yardim-cerceve { height: 100%; }
      .yardim-cerceve iframe { width: 100%; height: 100%; border: 0; display: block; }
    `,
  ],
})
export class YardimDugmesiComponent implements OnInit {
  /** Bu ekranın yayınlanmış yardım sayfası var mı. */
  hazir = false;
  adres: SafeResourceUrl | null = null;
  yukleniyor = false;
  private siteUrl = '';

  readonly araclar = [
    {
      widget: 'dxButton',
      toolbar: 'bottom',
      location: 'after',
      options: { text: 'Yeni sekmede aç', icon: 'export', stylingMode: 'text', onClick: () => this.yeniSekme() },
    },
  ];

  constructor(
    private el: ElementRef<HTMLElement>,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private yardimListesi: YardimListesiService,
  ) {}

  ngOnInit() {
    // data-yardim-yol sekme kutusunda, bileşen ondan önce DOM'a bağlanmış olur
    const form = this.formYolu();
    if (form) this.yardimListesi.hazirMi(form).then((v) => (this.hazir = v));
  }

  /** Arkadaki sekmeler visibility:hidden (dx-multiview-item-hidden); F1'i yalnız görünen sekmenin düğmesi alır. */
  @HostListener('document:keydown.f1', ['$event'])
  f1(olay: Event) {
    const e = this.el.nativeElement;
    if (!this.hazir || e.offsetParent === null || e.closest('.dx-multiview-item-hidden')) return;
    olay.preventDefault();
    if (!this.adres) this.ac();
  }

  async ac() {
    const form = this.formYolu();
    if (!form || !this.hazir || this.yukleniyor) return;
    this.yukleniyor = true;
    try {
      const taban = (environment.genService ?? '').replace(/\/+$/, '');
      const y = await firstValueFrom(this.http.post<YardimAnahtarYanit>(`${taban}/GEN/GenYardim/AnahtarOlustur`, { formAd: form }));
      if (!y?.url) {
        // Yetkisiz ekran ya da services'te "Yardim" ayarı yok: servis 200 + Result=false döner.
        notify(y?.errorMessage || 'Bu ekranın yardımı şu an açılamıyor.', 'warning', 4000);
        return;
      }
      this.siteUrl = y.siteUrl ?? '';
      this.adres = this.sanitizer.bypassSecurityTrustResourceUrl(y.url);
    } catch {
      // Ağ/sunucu hatası: genel hata yakalayıcı ayrıca gösterir.
      notify('Bu ekranın yardımı şu an açılamıyor.', 'warning', 4000);
    } finally {
      this.yukleniyor = false;
    }
  }

  kapat() {
    this.adres = null;
  }

  private yeniSekme() {
    if (this.siteUrl) window.open(this.siteUrl, '_blank', 'noopener,noreferrer');
  }

  /** Sekmenin yolu (/GEN/GenKisiForm); kayıt detayı sekmesinde (GenKisiFormx123/…) ana formun yolu. */
  private formYolu(): string | null {
    const yol = this.el.nativeElement.closest('[data-yardim-yol]')?.getAttribute('data-yardim-yol') ?? '';
    const ana = (DkNavService.detayAnaYolu(yol) ?? yol).split(/[?#]/)[0];
    return /^\/[A-Za-z0-9_]+\/[A-Za-z0-9_]+$/.test(ana) ? ana : null;
  }
}
